import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackPageChange } from '../socket';
import API from '../services/api';

// Page title mapping by path
const routeNameMap = {
  '/': 'Trang chủ',
  '/login': 'Đăng nhập',
  '/register': 'Đăng ký',
  '/request': 'Yêu cầu mượn xe',
  '/approval': 'Phê duyệt yêu cầu',
  '/return': 'Trả xe',
  '/incident': 'Báo cáo sự cố',
  '/incident-reports': 'Danh sách sự cố',
  '/report': 'Báo cáo',
  '/add-vehicle': 'Thêm xe mới',
  '/profile': 'Hồ sơ cá nhân',
  '/return-reports': 'Báo cáo trả xe',
  '/human-resources': 'Quản lý nhân sự',
  '/vehicle-management': 'Quản lý xe',
  '/work-journal': 'Nhật ký công việc',
  '/reception': 'Tiếp tân',
  '/guide': 'Hướng dẫn sử dụng',
  '/admin-work-management': 'Phạm vi công việc',
  '/support': 'Hỗ trợ',
  '/support-admin': 'Quản lý hỗ trợ',
  '/my-support-requests': 'Yêu cầu hỗ trợ của tôi',
  '/asset-management': 'Quản lý tài sản',
  '/superadmin': 'Quản trị viên',
  '/create-user': 'Tạo người dùng mới',
  '/all-notifications': 'Tất cả thông báo',
  '/driver-profiles': 'Hồ sơ tài xế',
  '/driver-schedule': 'Lịch trình tài xế',
  '/all-driver-schedules': 'Quản lý lịch trình tài xế',
  '/leave-request/create': 'Tạo đơn nghỉ phép',
  '/leave-requests': 'Quản lý đơn nghỉ phép',
  '/activity-logs': 'Nhật ký hoạt động',
  '/admin-notifications': 'Quản lý thông báo',
  '/admin/driver-management': 'Quản lý tài xế',
  '/fuel-management': 'Quản lý nhiên liệu',
  '/admin/announcements': 'Quản lý thông báo',
  '/announcements': 'Thông báo',
  '/privacy-policy': 'Chính sách bảo mật',
  '/todos': 'Sổ tay việc làm',
  '/company-documents': 'Tài liệu công ty',
  '/employee-evaluations': 'Đánh giá nhân viên',
  '/online-users': 'Người dùng trực tuyến',
  '/page-visibility': 'Hiển thị trang',
  '/notification-settings': 'Cài đặt thông báo',
  '/recruitment-dashboard': 'Bảng điều khiển tuyển dụng',
  '/recruitment-requests': 'Yêu cầu tuyển dụng',
  '/candidates': 'Ứng viên',
  '/interviews': 'Phỏng vấn',
  '/complete-profile': 'Hoàn thành hồ sơ',
  '/attendance-management': 'Quản lý chấm công',
  '/performance-management': 'Quản lý hiệu suất',
  '/super-admin/user-management': 'Quản lý người dùng',
  '/link-management': 'Quản lý liên kết',
  '/pickup-schedules': 'Quản lý lịch đón khách',
  '/probationary-management': 'Quản lý thử việc',
  '/birthdays': 'Sinh nhật',
  '/pdf-management': 'Quản lý PDF',
  '/software-rating-management': 'Quản lý đánh giá phần mềm',
  '/contracts': 'Hợp đồng',
  '/company-regulations': 'Nội quy công ty',
  '/admin/zalo-followers': 'Quản lý Zalo followers',
  '/admin/zalo-connection-requests': 'Yêu cầu kết nối Zalo'
};

// Get page title from path
const getPageTitle = (pathname) => {
  // Check exact match first
  if (routeNameMap[pathname]) {
    return routeNameMap[pathname];
  }

  // Check for dynamic routes (with IDs)
  const pathParts = pathname.split('/').filter(Boolean);
  if (pathParts.length >= 2) {
    const basePath = '/' + pathParts[0];
    if (routeNameMap[basePath]) {
      return routeNameMap[basePath] + ' - Chi tiết';
    }
  }

  // Fallback: convert path to readable name
  const lastPart = pathParts[pathParts.length - 1] || 'Trang chủ';
  return lastPart
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Component to track user page navigation and send to WebSocket
const WebSocketTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Track page changes
  useEffect(() => {
    if (user && location) {
      const hasSession = Boolean(localStorage.getItem('user'));
      if (!hasSession) return;

      // Get page title from our mapping (more reliable than document.title)
      const pageTitle = getPageTitle(location.pathname);

      // Send page change event to WebSocket
      trackPageChange(location.pathname, pageTitle);

      // Also send to API for backup tracking
      try {
        API.post('/user-activity/update', {
          currentPage: location.pathname,
          currentPageTitle: pageTitle
        }, {
          timeout: 10000,
          retry: 0,
          skipErrorInterceptorLog: true
        }).catch(err => {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            return;
          }
          const isTransient = (
            err?.code === 'ERR_NETWORK'
            || err?.code === 'ECONNABORTED'
            || [502, 503, 504].includes(err?.response?.status)
          );
          if (!isTransient) {
            console.error('Error updating user activity via API:', err);
          }
        });
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          return;
        }
        const isTransient = (
          error?.code === 'ERR_NETWORK'
          || error?.code === 'ECONNABORTED'
          || [502, 503, 504].includes(error?.response?.status)
        );
        if (!isTransient) {
          console.error('Error sending activity update:', error);
        }
      }
    }
  }, [location, user]);

  // This is a tracking component, so it doesn't render anything
  return null;
};

export default WebSocketTracker;
